/**
 * PageMode - Page-by-page reader mode
 * Location: /app/frontend/src/components/page-builder/reader/modes/
 * 
 * Phase 3-B: Enhanced with tap zones, keyboard navigation, progress persistence
 * - Mobile: Tap left/right zones to navigate
 * - Desktop: Click zones + keyboard arrows
 * - Center tap toggles header
 */

import React, { useEffect, useCallback, useMemo } from 'react';
import { useReaderContext, loadProgress } from '../hooks/useReaderContext';
import { sanitizeHtml, textToHtml, stripHtml } from '../hooks/sanitizeHtml';
import { Loader2, ImageOff, FileText, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../../../ui/button';

// Chunk text into pages (for novels)
const chunkTextIntoPages = (text, wordsPerPage = 300) => {
  if (!text) return [];
  
  const words = text.split(/\s+/);
  const pages = [];
  
  for (let i = 0; i < words.length; i += wordsPerPage) {
    pages.push(words.slice(i, i + wordsPerPage).join(' '));
  }
  
  return pages;
};

const PageMode = () => {
  const { 
    content, 
    metadata, 
    isLoading, 
    error, 
    contentType,
    contentId,
    unitId,
    currentPage,
    nextPage,
    prevPage,
    goToPage,
    totalPages,
    setProgress,
    toggleHeader,
    isMobile,
    shellSettings,
  } = useReaderContext();

  // Compute pages for novels
  const novelPages = useMemo(() => {
    if (contentType !== 'novel' || !content) return [];
    
    const text = content.contentHtml 
      ? stripHtml(content.contentHtml)
      : content.text || '';
    
    return chunkTextIntoPages(text, isMobile ? 150 : 250);
  }, [content, contentType, isMobile]);

  // Effective total pages
  const effectiveTotalPages = contentType === 'comic' 
    ? (content?.pages?.length || 0)
    : novelPages.length;

  // Load saved progress on mount
  useEffect(() => {
    if (!isLoading && content) {
      const saved = loadProgress(contentType, contentId, unitId, 'page');
      if (saved?.index !== undefined && saved.index < effectiveTotalPages) {
        goToPage(saved.index);
      }
    }
  }, [isLoading, content, contentType, contentId, unitId, effectiveTotalPages, goToPage]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'ArrowRight':
      case ' ':
        e.preventDefault();
        nextPage();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        prevPage();
        break;
      case 'Escape':
        e.preventDefault();
        toggleHeader();
        break;
      default:
        break;
    }
  }, [nextPage, prevPage, toggleHeader]);

  // Attach keyboard listener (controlled by enableKeyboardNav)
  useEffect(() => {
    if (shellSettings?.enableKeyboardNav === false) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, shellSettings?.enableKeyboardNav]);

  // Tap zone click handler
  const handleTapZone = (zone) => {
    if (zone === 'left') {
      prevPage();
    } else if (zone === 'right') {
      nextPage();
    } else {
      toggleHeader();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-6">
        <ImageOff className="w-16 h-16 text-red-500 mb-4" />
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-6">
        <FileText className="w-16 h-16 text-zinc-600 mb-4" />
        <p className="text-zinc-400">No content available</p>
      </div>
    );
  }

  // Comic: show one page at a time with tap zones
  if (contentType === 'comic' && content.pages) {
    const page = content.pages[currentPage];
    const canGoNext = currentPage < content.pages.length - 1;
    const canGoPrev = currentPage > 0;

    return (
      <div 
        className="page-reader relative h-[calc(100vh-56px)] flex items-center justify-center bg-black select-none"
        data-testid="page-mode-comic"
      >
        {/* Tap zones (invisible) */}
        <div 
          className="absolute left-0 top-0 w-[40%] h-full cursor-pointer z-10"
          onClick={() => handleTapZone('left')}
          aria-label="Previous page"
        />
        <div 
          className="absolute left-[40%] top-0 w-[20%] h-full cursor-pointer z-10"
          onClick={() => handleTapZone('center')}
          aria-label="Toggle header"
        />
        <div 
          className="absolute right-0 top-0 w-[40%] h-full cursor-pointer z-10"
          onClick={() => handleTapZone('right')}
          aria-label="Next page"
        />

        {/* Page display */}
        <div className="page-container max-h-[85vh] max-w-full flex items-center justify-center px-4">
          {page?.image_url || page?.url || page?.src ? (
            <img
              src={page.image_url || page.url || page.src}
              alt={`Page ${currentPage + 1}`}
              className="max-h-[85vh] max-w-full w-auto h-auto object-contain"
              draggable={false}
            />
          ) : (
            <div className="w-[60vw] h-[80vh] max-w-lg bg-zinc-800 flex items-center justify-center rounded-lg">
              <ImageOff className="w-16 h-16 text-zinc-600" />
            </div>
          )}
        </div>

        {/* Desktop navigation buttons */}
        {!isMobile && (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={prevPage}
              disabled={!canGoPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 w-12 h-12 rounded-full disabled:opacity-30"
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={nextPage}
              disabled={!canGoNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 w-12 h-12 rounded-full disabled:opacity-30"
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </Button>
          </>
        )}

        {/* Page counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 px-4 py-2 rounded-full z-20">
          <span className="text-white text-sm font-medium">
            {currentPage + 1} / {content.pages.length}
          </span>
        </div>

        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800 z-20">
          <div 
            className="h-full bg-orange-500 transition-all duration-300"
            style={{ width: `${((currentPage + 1) / content.pages.length) * 100}%` }}
          />
        </div>

        {/* Keyboard hint (desktop only) */}
        {!isMobile && (
          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 text-zinc-600 text-xs z-20">
            ← → Arrow keys • Esc toggle header
          </div>
        )}
      </div>
    );
  }

  // Novel: paginated text with tap zones
  if (contentType === 'novel' && (content.text || content.contentHtml)) {
    const pageText = novelPages[currentPage] || '';
    const canGoNext = currentPage < novelPages.length - 1;
    const canGoPrev = currentPage > 0;

    return (
      <div 
        className="page-reader relative h-[calc(100vh-56px)] flex items-center justify-center bg-black select-none"
        data-testid="page-mode-novel"
      >
        {/* Tap zones */}
        <div 
          className="absolute left-0 top-0 w-[40%] h-full cursor-pointer z-10"
          onClick={() => handleTapZone('left')}
          aria-label="Previous page"
        />
        <div 
          className="absolute left-[40%] top-0 w-[20%] h-full cursor-pointer z-10"
          onClick={() => handleTapZone('center')}
          aria-label="Toggle header"
        />
        <div 
          className="absolute right-0 top-0 w-[40%] h-full cursor-pointer z-10"
          onClick={() => handleTapZone('right')}
          aria-label="Next page"
        />

        {/* Text display */}
        <div className="max-w-2xl mx-auto px-8 py-6 text-center">
          <p className="text-zinc-200 text-lg md:text-xl leading-relaxed">
            {pageText}
          </p>
        </div>

        {/* Desktop navigation buttons */}
        {!isMobile && (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={prevPage}
              disabled={!canGoPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 w-12 h-12 rounded-full disabled:opacity-30"
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={nextPage}
              disabled={!canGoNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-black/50 hover:bg-black/70 w-12 h-12 rounded-full disabled:opacity-30"
            >
              <ChevronRight className="w-8 h-8 text-white" />
            </Button>
          </>
        )}

        {/* Page counter */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 px-4 py-2 rounded-full z-20">
          <span className="text-white text-sm font-medium">
            Page {currentPage + 1} / {novelPages.length}
          </span>
        </div>

        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-800 z-20">
          <div 
            className="h-full bg-orange-500 transition-all duration-300"
            style={{ width: `${((currentPage + 1) / novelPages.length) * 100}%` }}
          />
        </div>

        {/* Keyboard hint */}
        {!isMobile && (
          <div className="absolute bottom-12 left-1/2 -translate-x-1/2 text-zinc-600 text-xs z-20">
            ← → Arrow keys • Esc toggle header
          </div>
        )}
      </div>
    );
  }

  // Fallback
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center p-6">
      <FileText className="w-16 h-16 text-zinc-600 mb-4" />
      <p className="text-zinc-400">Unable to display content in page mode</p>
    </div>
  );
};

export default PageMode;
